import React, { useEffect, useRef, useState } from 'react';
import OTPInput from './OTPInput';
import { isUndefined, isEmpty } from 'lodash';
import Typography from '~/components/base/Typography';
import { useAppDispatch } from '~/redux/hooks';
import { setModalContent, clearModalContent } from '~/redux/modules/modalEvent';
import { onGetLetters, onGetNumberOnly } from '~/utils/helpers';
import Button from '~/components/base/Button';
import useIsFocused from '~/hooks/useIsFocused';

type Props = {
    className?: string,
    value: string,
    onChange: (value: any) => void,
    message?: string,
    label?: string,
    isLetters?: boolean,
    numberOfInputs?: number,
    defaultValue?: string,
    /**
     * Offer the clipboard candidate inline, under the boxes, instead of in a
     * modal.
     *
     * Required whenever this input is itself inside a modal: the prompt is
     * dispatched through the same single-slot modal slice, so it would replace
     * the dialog holding this input rather than appear over it. The view
     * already renders the inline affordance whenever `clipboard` is set.
     */
    isInLineClipboard?: boolean
}

export default function OTPInputContainer({ className = '', onChange, message, label, isLetters, numberOfInputs = 4, defaultValue, isInLineClipboard }: Props): JSX.Element {
    // Seeded from numberOfInputs, not from four. An effect below resets otp
    // whenever numberOfInputs changes, but the refs array is only ever written
    // by the view, so a short initial array left the later boxes unreachable
    // for focus advancement on the first render.
    const [otp, setOtp] = useState(() => Array.from({ length: numberOfInputs }, () => ''));
    const [clipboard, setClipboard] = useState('');
    const inputInputs = useRef<HTMLInputElement[] | null[]>(
        Array.from({ length: numberOfInputs }, () => null)
    );

    const dispatch = useAppDispatch();
    const isFocused = useIsFocused();

    const onChangeLetters = (position: number, value: string) => {
        if (value?.length > 2) {
            setOtp(otp => {
                return otp.map((val, index) => {
                    if (!isUndefined(value[index])) {
                        if (index < numberOfInputs - 1) inputInputs.current[index + 1]?.focus();

                        return value[index];
                    }

                    return val;
                });
            });

            return;
        }

        setOtp(otp => {
            return otp.map((val, index) => {
                if (index === position) {
                    if (isEmpty(value)) return value;

                    if (value.length > 1) {
                        return value[value.length - 1];
                    }

                    return !isEmpty(value) ? value : val;
                }

                return val;
            });
        });

        if (isEmpty(value?.trim()) && position > 0) {
            inputInputs.current[position - 1]?.focus();
        }

        if (!isEmpty(value?.trim()) && position < numberOfInputs) {
            inputInputs.current[position + 1]?.focus();
        }
    };

    const onHandleChange = (position: number, value: string) => {
        if (isLetters) {
            onChangeLetters(position, value);

            return;
        }

        const numericText = onGetNumberOnly(value);

        if (numericText?.length > 2) {
            setOtp(otp => {
                return otp.map((val, index) => {
                    if (!isUndefined(numericText[index]) && isFinite(parseInt(numericText[index]))) {
                        if (index < numberOfInputs - 1) inputInputs.current[index + 1]?.focus();

                        return numericText[index];
                    }

                    return val;
                });
            });

            return;
        }

        setOtp(otp => {
            return otp.map((val, index) => {
                if (index === position) {
                    if (isEmpty(numericText)) return numericText;

                    if (numericText.length > 1 && isFinite(parseInt(numericText[numericText.length - 1]))) {
                        return numericText[numericText.length - 1];
                    }

                    return isFinite(parseInt(numericText)) ? numericText : val;
                }

                return val;
            });
        });

        if (isEmpty(numericText?.trim()) && position > 0) {
            inputInputs.current[position - 1]?.focus();
        }

        if (!isEmpty(numericText?.trim()) && position < numberOfInputs) {
            inputInputs.current[position + 1]?.focus();
        }
    };

    /**
     * Move focus back a box when Backspace is pressed on an empty one.
     *
     * This cannot live in onHandleChange. An empty input whose value does not
     * change fires no change event at all, so pressing Backspace on an already
     * empty box reaches nothing -- which is why focus used to stay put and the
     * member had to click back a box themselves.
     *
     * A box that still holds a character is left entirely alone: the browser
     * deletes it, the change handler sees '' and moves focus from there, so a
     * second press walks back again. One press, one box, in both cases.
     *
     * @param {Number} position the index of the box the key was pressed in
     * @param {React.KeyboardEvent<HTMLInputElement>} event the keydown event
     * @returns {void}
     */
    const onHandleKeyDown = (position: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Backspace') return;

        if (!isEmpty(event.currentTarget.value)) return;

        if (position === 0) return;

        // Without this the keypress also deletes in the box we are moving to,
        // so a single Backspace would clear a character the member meant to
        // keep.
        event.preventDefault();

        inputInputs.current[position - 1]?.focus();
    };

    const onPaste = async (text: string) => {
        setOtp(otp => {
            return otp.map((val, index) => {
                if (!isUndefined(text[index])) {
                    return text[index];
                }

                return val;
            });
        });
        inputInputs.current.map(input => {
            input?.blur();
        });
    };

    const onSetClipboard = async () => {
        // Rejects outright when the document is not focused, and can be denied
        // by permissions. Neither is an error worth surfacing — there is simply
        // no candidate to offer.
        let copiedContent = '';

        try {
            if (!document.hasFocus()) return;

            copiedContent = await navigator.clipboard.readText();
        } catch {
            return;
        }

        const numericText = onGetNumberOnly(copiedContent);

        if (isInLineClipboard) {
            // Only offer something that could actually be a code. Without the
            // length check the prompt reads "Paste OTP code (someStrayText)"
            // for whatever happens to be on the clipboard, and accepting it
            // drops those characters into the boxes.
            setClipboard(numericText?.length === numberOfInputs ? numericText : '');

            return;
        }

        if (numericText === otp.join()) return;

        if (!isEmpty(numericText?.trim()) && numericText.length >= numberOfInputs) {
            const text = numericText.substring(0, numberOfInputs);

            setClipboard(text);

            dispatch(setModalContent({
                modalContent: <div className='o-t-p-input__modal'>
                    <Typography
                        color='dark'
                        size={16}
                        weight='semi-bold'
                        numberOfLines={2}>
                        OTP Code
                    </Typography>
                    <Typography
                        color='dark'
                        size={14}
                        className='o-t-p-input__modal-content'>
                        Paste OTP code <b>({text})</b> from your clipboard?
                    </Typography>
                    <Button
                        label='Yes'
                        variant='link'
                        style={{ marginTop: 'auto' }}
                        className='o-t-p-input__modal-button'
                        onClick={() => {
                            onPaste(text);
                            dispatch(clearModalContent());
                        }} />
                </div>
            }));
        } else {
            setClipboard('');
        }
    };

    const onSetClipboardText = async () => {
        // Rejects outright when the document is not focused, and can be denied
        // by permissions. Neither is an error worth surfacing — there is simply
        // no candidate to offer.
        let copiedContent = '';

        try {
            if (!document.hasFocus()) return;

            copiedContent = await navigator.clipboard.readText();
        } catch {
            return;
        }

        const lettersOnly = onGetLetters(copiedContent);

        if (isInLineClipboard) {
            // Only offer something that could actually be a code. Without the
            // length check the prompt reads "Paste OTP code (someStrayText)"
            // for whatever happens to be on the clipboard, and accepting it
            // drops those characters into the boxes.
            setClipboard(lettersOnly?.length === numberOfInputs ? lettersOnly : '');

            return;
        }

        if (lettersOnly === otp.join()) return;

        if (!isEmpty(lettersOnly?.trim()) && lettersOnly.length >= numberOfInputs) {
            const text = lettersOnly.substring(0, numberOfInputs);

            setClipboard(text);

            dispatch(setModalContent({
                modalContent: <div className='o-t-p-input__modal'>
                    <Typography
                        color='dark'
                        size={16}
                        weight='semi-bold'
                        numberOfLines={2}>
                        OTP Code
                    </Typography>
                    <Typography
                        color='dark'
                        size={14}
                        className='o-t-p-input__modal-content'>
                        Paste OTP code <b>({text})</b> from your clipboard?
                    </Typography>
                    <Button
                        label='Yes'
                        variant='link'
                        style={{ marginTop: 'auto' }}
                        className='o-t-p-input__modal-button'
                        onClick={() => {
                            onPaste(text);
                            dispatch(clearModalContent());
                        }} />
                </div>
            }));
        } else {
            setClipboard('');
        }
    };

    useEffect(() => {
        onChange(otp.join(''));
    }, [otp]);

    useEffect(() => {
        if (isFocused) {
            if (isLetters) {
                onSetClipboardText();
            } else {
                onSetClipboard();
            }
        }
    }, [isFocused, isLetters, isInLineClipboard]);

    useEffect(() => {
        setOtp(Array.from({ length: numberOfInputs }, () => '') as string[]);
    }, [numberOfInputs]);

    useEffect(() => {
        if (defaultValue) {
            setOtp(defaultValue.split(''));
        }
    }, [defaultValue]);

    return (
        <OTPInput
            className={className}
            value={otp}
            onChange={onHandleChange}
            onKeyDown={onHandleKeyDown}
            inputInputs={inputInputs}
            clipboard={clipboard}
            onPaste={onPaste}
            message={message}
            label={label}
            isLetters={isLetters} />
    );
}
